import React, { useMemo, useState } from 'react';
import { Extraction } from '../api/client';

interface Props {
  extractions: Extraction[];
  selectedId?: string | null;
  onSelect?: (id: string) => void;
  loading?: boolean;
}

// ─── Status config ────────────────────────────────────────────────────────────
const statusConfig: Record<string, { bg: string; text: string; dot: string }> = {
  COMPLETED:    { bg: '#DFF6DD', text: '#107C10', dot: '#107C10' },
  RECEIVED:     { bg: '#DFF6DD', text: '#107C10', dot: '#107C10' },
  TASK_CREATED: { bg: '#EBF3FB', text: '#0078D4', dot: '#0078D4' },
  FAILED:       { bg: '#FDE7E9', text: '#A4262C', dot: '#A4262C' },
  PROCESSING:   { bg: '#FFF4CE', text: '#8A8000', dot: '#C8A600' },
  PENDING:      { bg: '#F3F2F1', text: '#605E5C', dot: '#8A8886' },
};

function initials(name: string): string {
  return name.split(' ').map((w) => w[0] ?? '').slice(0, 2).join('').toUpperCase() || '?';
}

function avatarColor(str: string): string {
  const colors = ['#0078D4','#107C10','#5C2D91','#8764B8','#C43E1C','#038387','#004E8C','#69797E'];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
  if (diffDays === 0)  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (diffDays === 1)  return 'Yesterday';
  if (diffDays < 7)    return d.toLocaleDateString(undefined, { weekday: 'short' });
  if (diffDays < 365)  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function getPreview(e: Extraction): string {
  if (e.recipients?.length)
    return `To: ${e.recipients.slice(0, 2).join(', ')}${e.recipients.length > 2 ? ` +${e.recipients.length - 2}` : ''}`;
  return e.mailboxEmail ?? '';
}

// ─── Thread group ─────────────────────────────────────────────────────────────
interface ThreadGroup {
  key: string;
  latest: Extraction;
  rest: Extraction[];
  isThread: boolean;
}

function buildGroups(extractions: Extraction[]): ThreadGroup[] {
  const map = new Map<string, Extraction[]>();
  for (const e of extractions) {
    const key = e.conversationId ?? `__solo__${e.id}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(e);
  }
  return Array.from(map.values()).map((emails) => {
    const [latest, ...rest] = emails;
    return { key: latest.conversationId ?? `__solo__${latest.id}`, latest, rest, isThread: rest.length > 0 };
  });
}

// ─── Icons ────────────────────────────────────────────────────────────────────
const IconAttach = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.47"/>
  </svg>
);

// ─── Skeleton ─────────────────────────────────────────────────────────────────
const SkeletonRow: React.FC = () => (
  <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)', display: 'flex', gap: '10px', alignItems: 'center' }}>
    <div className="skeleton" style={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0 }} />
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <div className="skeleton" style={{ height: 12, width: '45%' }} />
        <div className="skeleton" style={{ height: 11, width: 48, flexShrink: 0 }} />
      </div>
      <div className="skeleton" style={{ height: 12, width: '75%' }} />
      <div className="skeleton" style={{ height: 11, width: '60%' }} />
    </div>
  </div>
);

// ─── Shared status badge ──────────────────────────────────────────────────────
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const sc = statusConfig[status] ?? statusConfig.PENDING;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '3px',
      background: sc.bg, color: sc.text, borderRadius: '2px',
      padding: '1px 6px', fontSize: '10px', fontWeight: 600, letterSpacing: '.03em', whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 4, height: 4, borderRadius: '50%', background: sc.dot, display: 'inline-block', flexShrink: 0 }} />
      {status}
    </span>
  );
};

// ─── Thread header row ────────────────────────────────────────────────────────
const ThreadHeader: React.FC<{
  group: ThreadGroup;
  isSelected: boolean;
  isExpanded: boolean;
  selectedId?: string | null;
  onSelect: (id: string) => void;
  onToggle: (e: React.MouseEvent) => void;
}> = ({ group, isSelected, isExpanded, selectedId, onSelect, onToggle }) => {
  const { latest, rest, isThread } = group;
  const anyUnread = !latest.isRead || rest.some((r) => !r.isRead);
  const color = avatarColor(latest.sender || latest.senderEmail);
  const threadSelected = isSelected || rest.some((r) => r.id === selectedId);

  const participants = isThread
    ? [...new Map([latest, ...rest].map((e) => [e.senderEmail, e.sender || e.senderEmail])).values()]
    : null;

  return (
    <div
      className={`email-row${threadSelected ? ' active' : ''}${anyUnread ? ' unread' : ''}`}
      onClick={() => onSelect(latest.id)}
      style={{ borderBottom: isExpanded && isThread ? 'none' : undefined }}
    >
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '11px 14px 10px' }}>

        {/* Unread dot */}
        <div style={{
          width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
          background: anyUnread && !threadSelected ? 'var(--accent)' : 'transparent',
        }} />

        {/* Avatar group */}
        <div style={{ position: 'relative', width: 36, height: 36, flexShrink: 0 }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%', background: color, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '12px', fontWeight: 700, letterSpacing: '.03em',
          }}>
            {initials(latest.sender || latest.senderEmail)}
          </div>
          {isThread && (
            <div style={{
              position: 'absolute', bottom: -3, right: -5,
              background: 'var(--accent)', color: '#fff',
              borderRadius: '8px', padding: '0 4px',
              fontSize: '9px', fontWeight: 800,
              border: '2px solid var(--card)', lineHeight: '14px',
              minWidth: 16, textAlign: 'center',
            }}>
              {rest.length + 1}
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>

          {/* Row 1: senders + time */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '6px', marginBottom: '2px' }}>
            <span style={{
              fontSize: '13px', fontWeight: anyUnread ? 700 : 500, color: 'var(--text)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {participants ? participants.slice(0, 3).join('; ') : (latest.sender || latest.senderEmail)}
            </span>
            <span style={{ fontSize: '11px', color: anyUnread ? 'var(--accent)' : 'var(--text-3)', fontWeight: anyUnread ? 600 : 400, flexShrink: 0, whiteSpace: 'nowrap' }}>
              {formatTime(latest.receivedAt)}
            </span>
          </div>

          {/* Row 2: subject */}
          <div style={{
            fontSize: '13px', fontWeight: anyUnread ? 600 : 400, color: 'var(--text)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '4px',
          }}>
            {latest.subject || '(No Subject)'}
          </div>

          {/* Row 3: preview + badges + expand */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '5px' }}>
            <span style={{ fontSize: '12px', color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
              {getPreview(latest)}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
              {(latest.hasAttachments || rest.some((r) => r.hasAttachments)) && (
                <span style={{ color: 'var(--text-3)', display: 'flex' }}><IconAttach /></span>
              )}
              <StatusBadge status={latest.status} />
              {isThread && (
                <button
                  onClick={onToggle}
                  title={isExpanded ? 'Collapse thread' : `Show all ${rest.length + 1} messages`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '3px',
                    padding: '2px 7px',
                    border: `1px solid ${isExpanded ? 'var(--accent)' : 'var(--border)'}`,
                    borderRadius: '4px',
                    background: isExpanded ? 'var(--accent)' : 'transparent',
                    color: isExpanded ? '#fff' : 'var(--text-3)',
                    fontSize: '10px', fontWeight: 700, cursor: 'pointer',
                    transition: 'all .12s',
                  }}
                  onMouseEnter={(ev) => {
                    if (!isExpanded) {
                      ev.currentTarget.style.borderColor = 'var(--accent)';
                      ev.currentTarget.style.color = 'var(--accent)';
                    }
                  }}
                  onMouseLeave={(ev) => {
                    if (!isExpanded) {
                      ev.currentTarget.style.borderColor = 'var(--border)';
                      ev.currentTarget.style.color = 'var(--text-3)';
                    }
                  }}
                >
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                    {isExpanded
                      ? <polyline points="18 15 12 9 6 15"/>
                      : <polyline points="6 9 12 15 18 9"/>}
                  </svg>
                  {rest.length + 1}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Thread children container ────────────────────────────────────────────────
const ThreadChild: React.FC<{
  email: Extraction;
  isSelected: boolean;
  isLast: boolean;
  onSelect: (id: string) => void;
}> = ({ email, isSelected, isLast, onSelect }) => {
  const isUnread = !email.isRead;
  const color = avatarColor(email.sender || email.senderEmail);

  return (
    <div
      className={`email-row${isSelected ? ' active' : ''}${isUnread ? ' unread' : ''}`}
      onClick={() => onSelect(email.id)}
      style={{
        borderBottom: isLast ? 'none' : '1px solid var(--border)',
        borderRadius: isLast ? '0 0 6px 6px' : 0,
      }}
    >
      <div style={{ display: 'flex', gap: '9px', alignItems: 'center', padding: '8px 14px 8px 14px' }}>

        {/* Unread dot */}
        <div style={{
          width: 5, height: 5, borderRadius: '50%', flexShrink: 0,
          background: isUnread && !isSelected ? 'var(--accent)' : 'transparent',
        }} />

        {/* Smaller avatar */}
        <div style={{
          width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
          background: color, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '9px', fontWeight: 700,
        }}>
          {initials(email.sender || email.senderEmail)}
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '6px', marginBottom: '2px' }}>
            <span style={{
              fontSize: '12.5px', fontWeight: isUnread ? 700 : 400, color: 'var(--text)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {email.sender || email.senderEmail}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
              {email.hasAttachments && <span style={{ color: 'var(--text-3)', display: 'flex' }}><IconAttach /></span>}
              <span style={{ fontSize: '11px', color: isUnread ? 'var(--accent)' : 'var(--text-3)', whiteSpace: 'nowrap' }}>
                {formatTime(email.receivedAt)}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
            <span style={{ fontSize: '12px', color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
              {getPreview(email)}
            </span>
            <StatusBadge status={email.status} />
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const EmailList: React.FC<Props> = ({ extractions, selectedId, onSelect, loading }) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const groups = useMemo(() => buildGroups(extractions), [extractions]);

  const toggleExpand = (key: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  if (loading) return <div>{Array.from({ length: 10 }).map((_, i) => <SkeletonRow key={i} />)}</div>;

  if (groups.length === 0) {
    return (
      <div style={{ padding: '64px 24px', textAlign: 'center' }}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#C8C6C4" strokeWidth="1.5" style={{ display: 'block', margin: '0 auto 12px' }}>
          <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
        </svg>
        <div style={{ fontWeight: 600, color: 'var(--text-2)', marginBottom: '4px', fontSize: '14px' }}>No messages</div>
        <div style={{ fontSize: '12px', color: 'var(--text-3)' }}>Try adjusting your search or filters</div>
      </div>
    );
  }

  return (
    <div>
      {groups.map((group) => {
        const isExpanded = expanded.has(group.key);
        const isSelected = selectedId === group.latest.id;

        return (
          <React.Fragment key={group.key}>
            <ThreadHeader
              group={group}
              isSelected={isSelected}
              isExpanded={isExpanded}
              selectedId={selectedId}
              onSelect={(id) => onSelect?.(id)}
              onToggle={(e) => toggleExpand(group.key, e)}
            />

            {/* Expanded thread children — Outlook-style grouped box */}
            {group.isThread && isExpanded && (
              <div style={{
                borderLeft: '3px solid var(--accent)',
                borderRight: '1px solid var(--border)',
                borderBottom: '1px solid var(--border)',
                borderRadius: '0 0 6px 6px',
                background: 'rgba(37,99,235,0.025)',
                marginBottom: '2px',
                overflow: 'hidden',
              }}>
                {group.rest.map((email, idx) => (
                  <ThreadChild
                    key={email.id}
                    email={email}
                    isSelected={selectedId === email.id}
                    isLast={idx === group.rest.length - 1}
                    onSelect={(id) => onSelect?.(id)}
                  />
                ))}
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default EmailList;
